import { WEATHER_CONFIG, getWeatherApiUrl } from '../config/weather';
import { applyBrandAssets } from '../utils/brandHelper';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

if (!API_BASE_URL) {
  console.error('REACT_APP_API_BASE_URL environment variable is not set');
}

let _handlingAuthError = false;

const authenticatedFetch = async (url, options = {}) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    const headers = { ...(options.headers || {}) };
    headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 && !_handlingAuthError && !url.includes('/login')) {
      _handlingAuthError = true;
      console.warn('Token expired or invalid — logging out');
      authAPI.logout();
      window.location.href = '/login';
      // Return a never-resolving promise to stop further processing
      return new Promise(() => {});
    }

    return response;
  }
  return fetch(url, options);
};

export const authAPI = {

  login: async (credentials) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });
      
      const result = await response.json();
      
      if (result.success) {
        if (result.token) {
          localStorage.setItem('authToken', result.token);
        }
        const userData = result.data || result.user;
        localStorage.setItem('loggedInUser', JSON.stringify(userData));
        if (result.company) {
          localStorage.setItem('companyBrand', JSON.stringify(result.company));
          applyBrandAssets();
        }
        return userData;
      } else {
        throw new Error(result.message || 'Bejelentkezési hiba');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },


  refreshUser: async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/me`);
      const result = await response.json();

      if (!result.success) {
        return null;
      }

      const userData = result.user;
      localStorage.setItem('loggedInUser', JSON.stringify(userData));

      if (result.company) {
        localStorage.setItem('companyBrand', JSON.stringify(result.company));
        applyBrandAssets();
      }

      return userData;
    } catch (error) {
      console.error('Refresh user error:', error);
      return null;
    }
  },

  logout: () => {
    localStorage.removeItem('loggedInUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('companyBrand');
  },


  getCurrentUser: () => {
    try {
      const userData = localStorage.getItem('loggedInUser');
      if (!userData || userData === 'undefined' || userData === 'null') {
        return null;
      }
      
      const user = JSON.parse(userData);
      if (!user || (!user.id && !user.user_id)) {
        localStorage.removeItem('loggedInUser');
        return null;
      }
      
      return user;
    } catch (error) {
      console.warn('User data parsing failed');
      localStorage.removeItem('loggedInUser');
      return null;
    }
  },


  isAuthenticated: () => {
    try {
      if (!localStorage.getItem('authToken')) {
        return false;
      }
      const userData = localStorage.getItem('loggedInUser');
      if (!userData || userData === 'undefined' || userData === 'null') {
        return false;
      }
      
      const user = JSON.parse(userData);
      return !!(user && (user.id || user.user_id));
    } catch (error) {
      console.warn('Authentication check failed');
      localStorage.removeItem('loggedInUser');
      return false;
    }
  },


  getCurrentUserId: () => {
    const user = authAPI.getCurrentUser();
    return user ? (user.user_id || user.id) : null;
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Hiba történt a jelszó visszaállítás kérésekor');
      }
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    }
  },

  resetPassword: async (token, password) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Hiba történt a jelszó megváltoztatásakor');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  },

  verifyResetToken: async (token) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/verify-reset-token?token=${token}`);
      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Érvénytelen token');
      }
    } catch (error) {
      console.error('Verify reset token error:', error);
      throw error;
    }
  },

  register: async (registrationData) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registrationData.name,
          tax_number: registrationData.taxNumber,
          email: registrationData.email,
          password: registrationData.password,
          skip_password_email: true
        }),
      });

      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Hiba történt a regisztráció során');
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }
};


export const companiesAPI = {

  getCompanies: async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-companies`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a cégek lekérésekor');
      }
    } catch (error) {
      console.error('Get companies error:', error);
      throw error;
    }
  }
};


export const vehiclesAPI = {

  getVehicleDocuments: async (vehicleId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-vehicle-documents/${vehicleId}`);
      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a dokumentumok lekérésekor');
      }
    } catch (error) {
      console.error('Get vehicle documents error:', error);
      throw error;
    }
  },

  getVehiclesByClient: async (clientId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-vehicles-by-client/${clientId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a járművek lekérésekor');
      }
    } catch (error) {
      console.error('Get vehicles by client error:', error);
      throw error;
    }
  },


  getClientCurrentVehicles: async (clientUserId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-client-current-vehicles/${clientUserId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a járművek lekérésekor');
      }
    } catch (error) {
      console.error('Get client vehicles error:', error);
      throw error;
    }
  },

  getClientVehicles: async (clientId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-vehicles-by-client/${clientId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba az ügyfél járműveinek lekérésekor');
      }
    } catch (error) {
      console.error('Get client vehicles error:', error);
      throw error;
    }
  },

  getVehiclesByOwner: async (ownerName, clientId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-vehicles-by-owner/${ownerName}/${clientId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a tulajdonos járműveinek lekérésekor');
      }
    } catch (error) {
      console.error('Get vehicles by owner error:', error);
      throw error;
    }
  },


  getVehicleAssignments: async (vehicleId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-vehicle-assignments/${vehicleId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a jármű hozzárendelések lekérésekor');
      }
    } catch (error) {
      console.error('Get vehicle assignments error:', error);
      throw error;
    }
  },


  getAllVehiclesForUser: async (user = null) => {
    try {
      const currentUser = user || authAPI.getCurrentUser();
      if (!currentUser) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }


      try {
        let vehicles;
        
        // Admin esetén az ügyfél összes autóját kérjük le
        if (currentUser.role === 'admin' || currentUser.role === 'boss') {
         
          const clientId = currentUser.client_id || currentUser.company_id || currentUser.company;
         
          vehicles = await vehiclesAPI.getClientVehicles(clientId);
        } else {

          vehicles = await vehiclesAPI.getClientCurrentVehicles(currentUser.user_id || currentUser.id);
        }

        return vehicles.map(vehicle => {
          // Get owner from vehicle assignments (directly from backend)
          let displayOwner = "Nincs hozzárendelve";
          let ownerId = null;

          // Get assignments from the vehicle object itself
          if (vehicle.assignments && vehicle.assignments.length > 0) {
            const assignment = vehicle.assignments[0];
            if (assignment.client_user_id && assignment.client_user_name) {
              ownerId = assignment.client_user_id;
              displayOwner = assignment.client_user_name;
            }
          }

          // Override with vehicle.owner if explicitly set
          if (vehicle.owner && vehicle.owner !== "Nincs hozzárendelve") {
            displayOwner = vehicle.owner;
          }
          
          // Use highway ticket data from backend if available
          let highwayTicketDisplay = vehicle.highway_ticket || "---";

          return {
            id: vehicle.id || vehicle.chassis_number, // Preserve the original ID or use chassis_number as fallback
            chassis_number: vehicle.chassis_number || "---",
            chassis_number17: vehicle.chassis_number17 || "---",
            plate_number: vehicle.plate_number || vehicle.licence_plate || "---",
            handover: vehicle.ivs_progress?.planned_handover || vehicle.handover || "---",
            handoff: vehicle.handoff || "---",
            monthly_distance: vehicle.rental_data?.km_start || "---",
            rentalFee: vehicle.rental_data?.monthly_fee ? `${vehicle.rental_data.monthly_fee} HUF` : "---",
            model: vehicle.model,
            brand: vehicle.brand,
            owner: displayOwner,
            owner_id: ownerId,
            highway_ticket: highwayTicketDisplay,
            highway_ticket_type: vehicle.highway_ticket_data?.type || null,
            highway_ticket_valid_to: vehicle.highway_ticket_data?.valid_to || null,
            status: vehicle.status || "---",
            status_info: vehicle.status_info || null,
            statusText: vehicle.status_info?.status_name || "---",
            company: vehicle.client_name || "---",
            gallery: vehicle.gallery || [],
            wheelType: vehicle.wheelType || "---",
            ivs_progress: vehicle.ivs_progress || null,
            issuer: vehicle.company_name || "---",
            issuerTax: vehicle.sa3_data?.issuer_tax || "---",
            issuedDate: vehicle.sa3_data?.issued_date || "---",
            deliveryDate: vehicle.sa3_data?.delivery_date || null,
            contractStart: vehicle.rental_data?.contract_start || null,
            contractEnd: vehicle.rental_data?.contract_end || null,
            latest_position: vehicle.latest_position || null,
            km_data: vehicle.km_data || null,
            obd: vehicle.obd || {
              instantConsumption: "---",
              avgConsumption: "---",
              fuelLevel: "---",
              km: "---",
              doable_distance: "---",
              moving: false,
              stopped: false
            },
          };
        });
      } catch (apiError) {
        console.error('API hiba:', apiError);
        throw apiError;
      }
    } catch (error) {
      console.error('Get all vehicles error:', error);
      throw error;
    }
  }
};


export const crashReportsAPI = {

  createCrashReport: async (data) => {
    try {

      const isFormData = data instanceof FormData;
      
      const requestOptions = {
        method: 'POST',
        body: data,
      };
      

      if (!isFormData) {
        requestOptions.headers = {
          'Content-Type': 'application/json',
        };
        requestOptions.body = JSON.stringify(data);
      }
      
      const response = await authenticatedFetch(`${API_BASE_URL}/create-crash-report`, requestOptions);
      
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a kárbejelentés létrehozásakor');
      }
    } catch (error) {
      console.error('Create crash report error:', error);
      throw error;
    }
  },


  getClientCrashReports: async (clientUserId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-client-crash-reports/${clientUserId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a kárbejelentések lekérésekor');
      }
    } catch (error) {
      console.error('Get client crash reports error:', error);
      throw error;
    }
  }
};


export const usersAPI = {

  getClientUsers: async (clientId = null) => {
    try {
      
      if (!clientId) {
        const currentUser = JSON.parse(localStorage.getItem('loggedInUser') || '{}');
        clientId = currentUser.client_id || currentUser.company_id || currentUser.company;
      }
      
      if (!clientId) {
        throw new Error('Client ID is required');
      }
      
      const response = await authenticatedFetch(`${API_BASE_URL}/client-users?client_id=${clientId}`);
      const result = await response.json();
      
      if (result.success) {
        return result.users;
      } else {
        throw new Error(result.message || 'Hiba a felhasználók lekérésekor');
      }
    } catch (error) {
      console.error('Get client users error:', error);
      throw error;
    }
  },

  createUser: async (userData) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const result = await response.json();

      if (result.success) {
        return result.data || result.user;
      } else {
        throw new Error(result.message || 'Hiba a felhasználó létrehozásakor');
      }
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  },

  updateUser: async (userId, userData) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/update-user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: userId, ...userData }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        return result.user;
      } else {
        throw new Error(result.message || 'Hiba a felhasználó frissítésekor');
      }
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/delete-user`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: userId }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Hiba a felhasználó törlésekor');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  },

  updateUserPermissions: async (userId, permissions) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/update-user-permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, ...permissions }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        return result.permissions;
      } else {
        throw new Error(result.message || 'Hiba a jogosultságok frissítésekor');
      }
    } catch (error) {
      console.error('Update user permissions error:', error);
      throw error;
    }
  },

  // Push Notification Methods
  subscribeToPush: async (fcmToken) => {
    try {
      const user = authAPI.getCurrentUser();
      if (!user || !user.id) {
        throw new Error('User not authenticated');
      }

      const response = await authenticatedFetch(`${API_BASE_URL}/push-subscription/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fcm_token: fcmToken,
          client_user_id: user.id
        }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Subscribe to push error:', error);
      throw error;
    }
  },

  unsubscribeFromPush: async () => {
    try {
      const user = authAPI.getCurrentUser();
      if (!user || !user.id) {
        throw new Error('User not authenticated');
      }

      const response = await authenticatedFetch(`${API_BASE_URL}/push-subscription/unsubscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_user_id: user.id
        }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Unsubscribe from push error:', error);
      throw error;
    }
  },

  sendTestPushNotification: async () => {
    try {
      const user = authAPI.getCurrentUser();
      if (!user || !user.id) {
        throw new Error('User not authenticated');
      }

      const response = await authenticatedFetch(`${API_BASE_URL}/push-subscription/test`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_user_id: user.id
        }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Send test push error:', error);
      throw error;
    }
  }
};

export const optionsAPI = {

  getReportReasonOptions: async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-report-reason-options`);
      const result = await response.json();
      
      if (result.success) {

        return Object.entries(result.data).map(([value, label]) => ({
          value,
          label
        }));
      } else {
        throw new Error(result.message || 'Hiba a bejelentés okok lekérésekor');
      }
    } catch (error) {
      console.error('Get report reason options error:', error);
      throw error;
    }
  },


  getStatusOptions: async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-status-options`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a státusz opciók lekérésekor');
      }
    } catch (error) {
      console.error('Get status options error:', error);
      throw error;
    }
  }
};


export const calendarAPI = {
  // Események lekérése
  getCalendarEvents: async (startDate, endDate, clientId = null, isBookable = null) => {
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      
      if (clientId) {
        params.append('client_id', clientId);
      }
      
      if (isBookable !== null) {
        params.append('is_bookable', isBookable ? 1 : 0);
      }
      
      const response = await authenticatedFetch(`${API_BASE_URL}/get-calendar-events?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();

      
      // Check for duplicate IDs
      if (result.success && result.data.events) {
        const ids = result.data.events.map(e => e.id);
        const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
        if (duplicateIds.length > 0) {
          console.warn('Duplicate event IDs found:', duplicateIds);
        }
      }
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba az események lekérésekor');
      }
    } catch (error) {
      console.error('Get calendar events error:', error);
      throw error;
    }
  },

  // Időpont foglalása
  bookTimeSlot: async (eventId, date, startTime, clientUserId = null) => {
    try {
      const requestBody = {
        event_id: eventId,
        date: date,
        start_time: startTime
      };
      
      if (clientUserId) {
        requestBody.client_user_id = clientUserId;
      }
      
      const response = await authenticatedFetch(`${API_BASE_URL}/book-time-slot`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba az időpont foglalásakor');
      }
    } catch (error) {
      console.error('Book time slot error:', error);
      throw error;
    }
  },

  // Időpont lemondása
  cancelTimeSlot: async (eventId, date, startTime) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/cancel-time-slot`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_id: eventId,
          date: date,
          start_time: startTime
        }),
      });

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba az időpont lemondásakor');
      }
    } catch (error) {
      console.error('Cancel time slot error:', error);
      throw error;
    }
  },

  // Elérhető időpontok lekérése
  getAvailableTimeSlots: async (eventId, date) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-available-time-slots?event_id=${eventId}&date=${date}`);
      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba az elérhető időpontok lekérésekor');
      }
    } catch (error) {
      console.error('Get available time slots error:', error);
      throw error;
    }
  }
};


export const weatherAPI = {
  // Get current weather for a location
  getCurrentWeather: async (lat, lon) => {
    try {
      const url = getWeatherApiUrl(WEATHER_CONFIG.ENDPOINTS.CURRENT_WEATHER, { lat, lon });
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        return {
          current: result.data.current,
          location: result.data.location
        };
      } else {
        throw new Error(result.message || 'Hiba az aktuális időjárás lekérésekor');
      }
    } catch (error) {
      if (error.message !== 'Failed to fetch') {
        console.error('Get current weather error:', error);
      }
      throw error;
    }
  },

  // Get weekly forecast for a location
  getWeeklyForecast: async (lat, lon) => {
    try {
      const url = getWeatherApiUrl(WEATHER_CONFIG.ENDPOINTS.WEEKLY_FORECAST, { lat, lon, days: 7 });
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        return result.data.forecast || result.data.daily || [];
      } else {
        throw new Error(result.message || 'Hiba az időjárás előrejelzés lekérésekor');
      }
    } catch (error) {
      if (error.message !== 'Failed to fetch') {
        console.error('Get weekly forecast error:', error);
      }
      throw error;
    }
  },

  // Get both current weather and forecast
  getWeatherData: async (lat, lon) => {
    try {
      const [currentWeatherData, forecast] = await Promise.all([
        weatherAPI.getCurrentWeather(lat, lon),
        weatherAPI.getWeeklyForecast(lat, lon)
      ]);

      // Ensure we have valid data structure
      if (currentWeatherData.current && currentWeatherData.location && Array.isArray(forecast)) {
        return {
          current: currentWeatherData.current,
          location: currentWeatherData.location,
          forecast: forecast
        };
      } else {
        throw new Error('Invalid weather data structure received');
      }
    } catch (error) {
      if (error.message !== 'Failed to fetch') {
        console.error('Get weather data error:', error);
      }
      throw error;
    }
  },

  // Get weather alerts for a location
  getWeatherAlerts: async (lat, lon) => {
    try {
      const url = getWeatherApiUrl(WEATHER_CONFIG.ENDPOINTS.WEATHER_ALERTS, { lat, lon });
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        return {
          alerts: result.data.alerts || [],
          count: result.data.count || 0,
          hasActiveAlerts: result.data.hasActiveAlerts || false,
          location: result.data.location
        };
      } else {
        throw new Error(result.message || 'Hiba az időjárási riasztások lekérésekor');
      }
    } catch (error) {
      if (error.message !== 'Failed to fetch') {
        console.error('Get weather alerts error:', error);
      }
      throw error;
    }
  }
};


export const bugReportsAPI = {
  /**
   * Create a new bug report
   * @param {Object} data - Bug report data
   * @param {string} data.subject - Subject of the bug report
   * @param {string} data.message - Message/description of the bug
   * @param {string} data.type - Type of bug report (general, notice, other)
   * @returns {Promise<Object>} API response
   */
  createBugReport: async (data) => {
    try {
      const clientUserId = authAPI.getCurrentUserId();
      
      if (!clientUserId) {
        throw new Error('User not authenticated');
      }

      const requestData = {
        subject: data.subject,
        message: data.message,
        type: data.type,
        client_user_id: clientUserId
      };


      const response = await authenticatedFetch(`${API_BASE_URL}/create-bug-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });


      const responseText = await response.text();


      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new Error(`Invalid response format. Expected JSON but got: ${responseText.substring(0, 100)}...`);
      }

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a hibajegy létrehozásakor');
      }
    } catch (error) {
      console.error('Create bug report error:', error);
      throw error;
    }
  },

  /**
   * Get bug reports for a client (company)
   * @param {number} clientId - ID of the client/company
   * @returns {Promise<Array>} Array of bug reports
   */
  getBugReports: async (clientId) => {
    try {
      if (!clientId) {
        throw new Error('Client ID is required');
      }

      const url = `${API_BASE_URL}/get-bug-reports/${clientId}`;


      const response = await authenticatedFetch(url);

      const responseText = await response.text();

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new Error(`Invalid response format. Expected JSON but got: ${responseText.substring(0, 100)}...`);
      }

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba a hibajegyek lekérésekor');
      }
    } catch (error) {
      console.error('Get bug reports error:', error);
      throw error;
    }
  },

  /**
   * Add a message to an existing bug report
   * @param {Object} data - Message data
   * @param {number} data.ticket_id - ID of the ticket
   * @param {string} data.message - Message content
   * @returns {Promise<Object>} API response
   */
  addBugReportMessage: async (data) => {
    try {
      const clientUserId = authAPI.getCurrentUserId();
      
      if (!clientUserId) {
        throw new Error('User not authenticated');
      }

      const requestData = {
        ticket_id: data.ticket_id,
        message: data.message,
        client_user_id: clientUserId
      };


      const response = await authenticatedFetch(`${API_BASE_URL}/add-bug-report-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });


      const responseText = await response.text();


      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new Error(`Invalid response format. Expected JSON but got: ${responseText.substring(0, 100)}...`);
      }

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Hiba az üzenet hozzáadásakor');
      }
    } catch (error) {
      console.error('Add bug report message error:', error);
      throw error;
    }
  }
};


export const highwayTicketAPI = {
  /**
   * Get highway ticket data by license plate
   * @param {string} plateNumber - License plate number
   * @returns {Promise<Object>} Highway ticket data
   */
  getByLicensePlate: async (plateNumber) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-highway-ticket/${plateNumber}`);
      const result = await response.json();
      
      if (result.success) {
        return result.highway_ticket;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Get highway ticket error:', error);
      return null;
    }
  },

  /**
   * Request highway ticket for vehicle
   * @param {string} plateNumber - License plate number
   * @returns {Promise<Object>} API response
   */
  requestTicket: async (plateNumber) => {
    try {
      const currentUser = authAPI.getCurrentUser();
      const clientUserId = currentUser ? (currentUser.user_id || currentUser.id) : null;

      const requestData = {
        plate_number: plateNumber,
        client_user_id: clientUserId,
        company_id: 1
      };


      const response = await authenticatedFetch(`${API_BASE_URL}/request-highway-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        return result;
      } else {
        throw new Error(result.error || 'Hiba az autópálya matrica igénylésekor');
      }
    } catch (error) {
      console.error('Request highway ticket error:', error);
      if (error.name === 'SyntaxError') {
        throw new Error('Szerver hiba: érvénytelen válasz formátum');
      }
      throw error;
    }
  }
};


export const apiHelpers = {

  createFormData: (data) => {
    const formData = new FormData();
    
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        if (Array.isArray(data[key])) {
          data[key].forEach(item => {
            formData.append(`${key}[]`, item);
          });
        } else {
          formData.append(key, data[key]);
        }
      }
    });
    
    return formData;
  },


  getAuthHeaders: () => {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  },


  handleApiError: (error) => {
    console.error('API Error:', error);
    if (error.message) {
      return error.message;
    }
    return 'Ismeretlen hiba történt';
  }
};

// Trip API
export const tripsAPI = {
  /**
   * Get trips for the current user's vehicles grouped by vehicle and month
   * @param {number} clientUserId - The client user ID
   * @param {number} year - Year (optional, defaults to current year)
   * @param {number} month - Month (1-12, optional, defaults to current month)
   * @returns {Promise<Object>} - Promise that resolves to trip data grouped by vehicle
   */
  getUserTrips: async (clientUserId, year = null, month = null) => {
    try {
      const params = new URLSearchParams({
        client_user_id: clientUserId
      });

      if (year !== null) params.append('year', year);
      if (month !== null) params.append('month', month);

      const response = await authenticatedFetch(`${API_BASE_URL}/get-user-trips?${params}`);
      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Nem sikerült betölteni az utakat');
      }
    } catch (error) {
      console.error('Error fetching user trips:', error);
      throw error;
    }
  },

  /**
   * Get detailed trip information including route points
   * @param {number} tripId - The trip ID
   * @returns {Promise<Object>} - Promise that resolves to detailed trip data
   */
  getTripDetails: async (tripId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/get-trip-details?trip_id=${tripId}`);
      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Nem sikerült betölteni az út részleteit');
      }
    } catch (error) {
      console.error('Error fetching trip details:', error);
      throw error;
    }
  },

  /**
   * Update trip information
   * @param {Object} data - Trip update data
   * @param {number} data.trip_id - The trip ID
   * @param {string} data.trip_type - Trip type (business/private)
   * @param {number} data.start_latitude - Start latitude
   * @param {number} data.start_longitude - Start longitude
   * @param {number} data.end_latitude - End latitude
   * @param {number} data.end_longitude - End longitude
   * @returns {Promise<Object>} - Promise that resolves to updated trip data
   */
  updateTrip: async (data) => {
    try {
      // Convert data to URLSearchParams for form-urlencoded
      const formData = new URLSearchParams();
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });

      const response = await authenticatedFetch(`${API_BASE_URL}/update-trip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      const responseText = await response.text();

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Érvénytelen válasz a szervertől: ${responseText.substring(0, 100)}`);
      }

      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Nem sikerült frissíteni az utat');
      }
    } catch (error) {
      console.error('Error updating trip:', error);
      throw error;
    }
  },

  /**
   * Generate route sheet Excel file
   * @param {Object} data - Route sheet data
   * @param {number} data.vehicle_id - Vehicle ID
   * @param {string} data.vehicle_plate - Vehicle plate number
   * @param {string} data.vehicle_brand - Vehicle brand
   * @param {string} data.vehicle_model - Vehicle model
   * @param {number} data.year - Year
   * @param {number} data.month - Month (1-12)
   * @param {Array<number>} data.trip_ids - Array of trip IDs to include
   * @param {string} data.user_name - Driver name
   * @returns {Promise<Blob>} - Promise that resolves to Excel file blob
   */
  generateRouteSheetExcel: async (data) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/generate-route-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response is JSON (error) or blob (file)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        throw new Error(result.message || 'Hiba a menetlevél generálása során');
      }

      // Return blob for file download
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Error generating route sheet:', error);
      throw error;
    }
  },

  /**
   * Generate route sheet PDF file
   * @param {Object} data - Route sheet data (same as Excel)
   * @returns {Promise<Blob>} - Promise that resolves to PDF file blob
   */
  generateRouteSheetPDF: async (data) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/generate-route-sheet-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response is JSON (error) or blob (file)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        throw new Error(result.message || 'Hiba a PDF menetlevél generálása során');
      }

      // Return blob for file download
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Error generating PDF route sheet:', error);
      throw error;
    }
  }
};

export default {
  auth: authAPI,
  companies: companiesAPI,
  vehicles: vehiclesAPI,
  crashReports: crashReportsAPI,
  users: usersAPI,
  options: optionsAPI,
  calendar: calendarAPI,
  weather: weatherAPI,
  highwayTicket: highwayTicketAPI,
  trips: tripsAPI,
  helpers: apiHelpers
}; 